/**
 * API Service
 * 
 * Centralized API communication layer
 * Handles all HTTP requests to the backend
 */

import axios from 'axios';

// Base API URL
// In production (Vercel), we use a relative path /api
// In development, we fallback to localhost if the env variable is not set
const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

/**
 * Create axios instance with default config
 */
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000, // 10 seconds
});

/**
 * Request Interceptor
 * Automatically adds JWT token to all requests
 */
apiClient.interceptors.request.use(
    (config) => {
        // Get token from localStorage
        const token = localStorage.getItem('codewave_token');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response Interceptor
 * Handles global errors and token expiration
 */
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Handle token expiration
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('codewave_token');
            localStorage.removeItem('codewave_user');

            // Redirect to login (you can use React Router here)
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

// ==================== QUESTION SERVICES ====================

/**
 * Add a question to a quiz
 * @param {string} quizId 
 * @param {Object} questionData 
 */
export const addQuestion = async (quizId, questionData) => {
    try {
        const response = await apiClient.post(`/quizzes/${quizId}/questions`, questionData);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update a question
 * @param {string} id 
 * @param {Object} updates 
 */
export const updateQuestion = async (id, updates) => {
    try {
        const response = await apiClient.put(`/quizzes/questions/${id}`, updates);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete a question
 * @param {string} id 
 */
export const deleteQuestion = async (id) => {
    try {
        const response = await apiClient.delete(`/quizzes/questions/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== AUTH SERVICES ====================

/**
 * Login
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<Object>} { token, user }
 */
export const login = async (email, password) => {
    try {
        const response = await apiClient.post('/auth/login', {
            email,
            password,
        });

        const { token, user } = response.data.data;

        // Save to localStorage
        localStorage.setItem('codewave_token', token);
        localStorage.setItem('codewave_user', JSON.stringify(user));

        return { token, user };
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get current user profile
 * @returns {Promise<Object>} User object
 */
export const getMe = async () => {
    try {
        const response = await apiClient.get('/auth/me');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Change password
 * @param {string} currentPassword 
 * @param {string} newPassword 
 */
export const changePassword = async (currentPassword, newPassword) => {
    try {
        const response = await apiClient.put('/auth/change-password', {
            currentPassword,
            newPassword,
        });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Logout (client-side only)
 */
export const logout = () => {
    localStorage.removeItem('codewave_token');
    localStorage.removeItem('codewave_user');
    window.location.href = '/login';
};

// ==================== LESSON SERVICES ====================

/**
 * Get all lessons
 * @param {Object} params - { track, isEnabled }
 * @returns {Promise<Array>} Array of lessons
 */
export const getLessons = async (params = {}) => {
    try {
        const response = await apiClient.get('/lessons', { params });
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get lesson by ID
 * @param {string} id 
 * @returns {Promise<Object>} Lesson object
 */
export const getLessonById = async (id) => {
    try {
        const response = await apiClient.get(`/lessons/${id}`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Create lesson (Admin only)
 * @param {Object} lessonData 
 * @returns {Promise<Object>} Created lesson
 */
export const createLesson = async (lessonData) => {
    try {
        const response = await apiClient.post('/lessons', lessonData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update lesson (Admin only)
 * @param {string} id 
 * @param {Object} lessonData 
 * @returns {Promise<Object>} Updated lesson
 */
export const updateLesson = async (id, lessonData) => {
    try {
        const response = await apiClient.put(`/lessons/${id}`, lessonData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete lesson (Admin only)
 * @param {string} id 
 */
export const deleteLesson = async (id) => {
    try {
        const response = await apiClient.delete(`/lessons/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== QUIZ SERVICES ====================

/**
 * Get all quizzes (filtered by user role/track)
 * @returns {Promise<Array>} Array of quizzes
 */
export const getQuizzes = async () => {
    try {
        const response = await apiClient.get('/quizzes');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get quiz by ID
 * @param {string} quizId 
 * @returns {Promise<Object>} Quiz object
 */
export const getQuizById = async (quizId) => {
    try {
        const response = await apiClient.get(`/quizzes/${quizId}`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Submit quiz attempt
 * @param {string} quizId 
 * @param {Object} answers - { questionId: 'A', ... }
 * @param {number} timeTaken - Minutes
 * @returns {Promise<Object>} Attempt result
 */
export const submitQuizAttempt = async (quizId, answers, timeTaken) => {
    try {
        const response = await apiClient.post(`/quizzes/${quizId}/submit`, {
            answers,
            timeTaken,
        });
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== STUDENT SERVICES ====================

/**
 * Get student dashboard
 * @returns {Promise<Object>} Dashboard data
 */
export const getStudentDashboard = async () => {
    try {
        const response = await apiClient.get('/students/dashboard');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get student's attempts for a quiz
 * @param {string} quizId 
 * @returns {Promise<Array>} Array of attempts
 */
export const getQuizAttempts = async (quizId) => {
    try {
        const response = await apiClient.get(`/students/quizzes/${quizId}/attempts`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Check if student can attempt quiz
 * @param {string} quizId 
 * @returns {Promise<Object>} { canAttempt, attemptCount, remainingAttempts }
 */
export const checkCanAttempt = async (quizId) => {
    try {
        const response = await apiClient.get(`/students/quizzes/${quizId}/can-attempt`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get student statistics
 * @returns {Promise<Object>} Statistics object
 */
export const getStudentStatistics = async () => {
    try {
        const response = await apiClient.get('/students/statistics');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get attempt details
 * @param {string} attemptId 
 * @returns {Promise<Object>} Attempt details
 */
export const getAttemptDetails = async (attemptId) => {
    try {
        const response = await apiClient.get(`/students/attempts/${attemptId}`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== AI QUIZ SERVICES ====================

export const checkAIQuizAttempt = async (lessonId) => {
    try {
        const response = await apiClient.get(`/ai-quiz/check/${lessonId}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const startAIQuiz = async (lessonId, questions) => {
    try {
        const response = await apiClient.post(`/ai-quiz/start/${lessonId}`, { questions });
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const submitAIQuiz = async (lessonId, answers) => {
    try {
        const response = await apiClient.post(`/ai-quiz/submit/${lessonId}`, { answers });
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== ADMIN SERVICES ====================

/**
 * Get global statistics for Admin Dashboard
 * @returns {Promise<Object>} Stats object
 */
export const getAdminStats = async () => {
    try {
        const response = await apiClient.get('/admin/stats');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get all attempts across platform (Admin only)
 * @returns {Promise<Array>} Array of attempts
 */
export const getAllAttempts = async () => {
    try {
        const response = await apiClient.get('/admin/attempts');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update student specific permissions (Admin only)
 * @param {string} studentId 
 * @param {Object} permissions - { allowedQuizzes: [], allowedCourses: [] }
 * @returns {Promise<Object>} Updated student
 */
export const updateStudentPermissions = async (studentId, permissions) => {
    try {
        const response = await apiClient.patch(`/admin/users/${studentId}/permissions`, permissions);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Create student account (Admin only)
 * @param {Object} studentData - { email, password, fullName, track }
 * @returns {Promise<Object>} Created student
 */
export const createStudent = async (studentData) => {
    try {
        const response = await apiClient.post('/users', studentData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get all users (Admin only)
 * @param {Object} filters - { role, track, isActive }
 * @returns {Promise<Array>} Array of users
 */
export const getAllUsers = async (filters = {}) => {
    try {
        const response = await apiClient.get('/users', { params: filters });
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Toggle user status (Admin only)
 * @param {string} userId 
 * @returns {Promise<Object>} Updated user
 */
export const toggleUserStatus = async (userId) => {
    try {
        const response = await apiClient.patch(`/users/${userId}/toggle-status`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Create quiz (Admin only)
 * @param {Object} quizData 
 * @returns {Promise<Object>} Created quiz
 */
export const createQuiz = async (quizData) => {
    try {
        const response = await apiClient.post('/quizzes', quizData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update quiz (Admin only)
 * @param {string} quizId 
 * @param {Object} quizData 
 * @returns {Promise<Object>} Updated quiz
 */
export const updateQuiz = async (quizId, quizData) => {
    try {
        const response = await apiClient.put(`/quizzes/${quizId}`, quizData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete quiz (Admin only)
 * @param {string} quizId 
 * @returns {Promise<Object>}
 */
export const deleteQuiz = async (quizId) => {
    try {
        const response = await apiClient.delete(`/quizzes/${quizId}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Toggle quiz status (Admin only)
 * @param {string} quizId 
 * @returns {Promise<Object>} Updated quiz
 */
export const toggleQuizStatus = async (quizId) => {
    try {
        const response = await apiClient.patch(`/quizzes/${quizId}/toggle`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get quiz statistics (Admin only)
 * @param {string} quizId 
 * @returns {Promise<Object>} Quiz statistics
 */
export const getQuizStatistics = async (quizId) => {
    try {
        const response = await apiClient.get(`/quizzes/${quizId}/statistics`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== NOTIFICATION SERVICES ====================

/**
 * Get current user notifications
 * @param {Object} params - { page, limit }
 * @returns {Promise<Object>} { data, count, total, unreadCount, pagination }
 */
export const getMyNotifications = async (params = {}) => {
    try {
        const response = await apiClient.get('/notifications', { params });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get unread notifications count
 * @returns {Promise<number>} count
 */
export const getUnreadNotificationsCount = async () => {
    try {
        const response = await apiClient.get('/notifications/unread-count');
        return response.data.count;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Mark notification as read
 * @param {string} id 
 * @returns {Promise<Object>} Updated notification
 */
export const markNotificationAsRead = async (id) => {
    try {
        const response = await apiClient.put(`/notifications/${id}/read`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Mark all notifications as read
 * @returns {Promise<boolean>} success
 */
export const markAllNotificationsAsRead = async () => {
    try {
        const response = await apiClient.put('/notifications/read-all');
        return response.data.success;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete a notification
 * @param {string} id 
 * @returns {Promise<boolean>} success
 */
export const deleteNotification = async (id) => {
    try {
        const response = await apiClient.delete(`/notifications/${id}`);
        return response.data.success;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Send system notification (Admin only)
 * @param {Object} data - { title, message, type, link, targetAudience }
 * @returns {Promise<Object>} Result
 */
export const sendSystemNotification = async (data) => {
    try {
        const response = await apiClient.post('/notifications/system', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== FINANCE SERVICES ====================

/**
 * Get all payments with filters
 * @param {Object} filters
 * @returns {Promise<Array>} Array of payments
 */
export const getPayments = async (filters = {}) => {
    try {
        const response = await apiClient.get('/payments', { params: filters });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Create a new payment
 * @param {Object} paymentData
 * @returns {Promise<Object>} Created payment
 */
export const createPayment = async (paymentData) => {
    try {
        const response = await apiClient.post('/payments', paymentData);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete a payment record
 * @param {string} id 
 */
export const deletePayment = async (id) => {
    try {
        const response = await apiClient.delete(`/payments/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update a payment record
 * @param {string} id 
 * @param {Object} data 
 */
export const updatePayment = async (id, data) => {
    try {
        const response = await apiClient.put(`/payments/${id}`, data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get financial summary
 * @returns {Promise<Object>} Summary data
 */
export const getFinancialSummary = async () => {
    try {
        const response = await apiClient.get('/payments/summary');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get student financial status (all students)
 * @returns {Promise<Array>} Array of students with financial data
 */
export const getStudentsFinancialStatus = async () => {
    try {
        const response = await apiClient.get('/payments/students-status');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get student detailed financial profile
 * @param {string} id 
 */
export const getStudentFinancialProfile = async (id) => {
    try {
        const response = await apiClient.get(`/payments/student/${id}`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update student total fees
 * @param {string} userId 
 * @param {number} totalFees 
 */
export const updateStudentFees = async (userId, totalFees) => {
    try {
        const response = await apiClient.put(`/users/${userId}`, { totalFees });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== ACTIVITY LOG SERVICES ====================

/**
 * Get system activity logs
 * @param {Object} params - { studentId, actionType, performedBy, startDate, endDate }
 */
export const getActivityLogs = async (params = {}) => {
    try {
        const response = await apiClient.get('/activity', { params });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== COLLECTOR WALLET SERVICES ====================

/**
 * Get collector balance summary
 */
export const getCollectorSummary = async () => {
    try {
        const response = await apiClient.get('/collectors/summary');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Create a new collector transaction (Outgoing)
 * @param {Object} data - { collectorName, type, amount, notes, category }
 */
export const createCollectorTransaction = async (data) => {
    try {
        const response = await apiClient.post('/collectors/transaction', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get transaction history for collectors
 */
export const getCollectorHistory = async (params = {}) => {
    try {
        const response = await apiClient.get('/collectors/history', {
            params
        });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Handle API errors
 * @param {Error} error 
 * @returns {Error} Formatted error (suitable for throw)
 */
export const handleError = (error) => {
    console.error('API Error:', error.response || error);
    const message = error.response?.data?.message || error.message || 'Something went wrong';
    return new Error(message);
};

// ==================== GROUP SERVICES ====================

/**
 * Get all groups
 * @returns {Promise<Array>} Array of groups
 */
export const getGroups = async () => {
    try {
        const response = await apiClient.get('/groups');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get group by ID
 * @param {string} id 
 */
export const getGroupById = async (id) => {
    try {
        const response = await apiClient.get(`/groups/${id}`);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Create a new group
 * @param {Object} groupData - { name, code, level, description }
 */
export const createGroup = async (groupData) => {
    try {
        const response = await apiClient.post('/groups', groupData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Update a group
 * @param {string} id 
 * @param {Object} groupData 
 */
export const updateGroup = async (id, groupData) => {
    try {
        const response = await apiClient.put(`/groups/${id}`, groupData);
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Toggle group status
 * @param {string} id 
 */
export const toggleGroupStatus = async (id) => {
    try {
        const response = await apiClient.delete(`/groups/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};


/**
 * Get current user from localStorage
 * @returns {Object|null} User object or null
 */
export const getCurrentUser = () => {
    try {
        const userStr = localStorage.getItem('codewave_user');
        return userStr ? JSON.parse(userStr) : null;
    } catch {
        return null;
    }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
    return !!localStorage.getItem('codewave_token');
};

/**
 * Check if user is admin
 * @returns {boolean}
 */
export const isAdmin = () => {
    const user = getCurrentUser();
    return user?.role === 'admin';
};

/**
 * Check if user is student
 * @returns {boolean}
 */
export const isStudent = () => {
    const user = getCurrentUser();
    return user?.role === 'student';
};

/**
 * Get user's track
 * @returns {string|null} 'scratch' or 'web' or null
 */
export const getUserTrack = () => {
    const user = getCurrentUser();
    return user?.track || null;
};

/**
 * Update a collector transaction
 */
export const updateCollectorTransaction = async (id, data) => {
    try {
        const response = await apiClient.put(`/collectors/transaction/${id}`, data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete a collector transaction
 */
export const deleteCollectorTransaction = async (id) => {
    try {
        const response = await apiClient.delete(`/collectors/transaction/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== PAYROLL SERVICES ====================

export const getPayrollGroups = async () => {
    try {
        const response = await apiClient.get('/payroll/groups');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const createPayrollGroup = async (data) => {
    try {
        const response = await apiClient.post('/payroll/groups', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const getPayrollInstructors = async () => {
    try {
        const response = await apiClient.get('/payroll/instructors');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const createPayrollInstructor = async (data) => {
    try {
        const response = await apiClient.post('/payroll/instructors', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const updatePayrollInstructor = async (id, data) => {
    try {
        const response = await apiClient.put(`/payroll/instructors/${id}`, data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const deletePayrollInstructor = async (id) => {
    try {
        const response = await apiClient.delete(`/payroll/instructors/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const recordPayrollSession = async (data) => {
    try {
        const response = await apiClient.post('/payroll/sessions', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const updatePayrollSessionStatus = async (id, status) => {
    try {
        const response = await apiClient.patch(`/payroll/sessions/${id}/status`, { status });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const recordPayrollPayment = async (data) => {
    try {
        const response = await apiClient.post('/payroll/payments', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const getPayrollOverview = async (cycle = '', instructorId = '') => {
    try {
        const response = await apiClient.get(`/payroll/overview?cycle=${cycle}&instructorId=${instructorId}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const closePayrollCycle = async (cycleName) => {
    try {
        const response = await apiClient.post('/payroll/close', { cycleName });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const reopenPayrollCycle = async (cycleName) => {
    try {
        const response = await apiClient.post('/payroll/reopen', { cycleName });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const archivePayrollCycle = async (cycleName) => {
    try {
        const response = await apiClient.post('/payroll/archive', { cycleName });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

export const getInstructorPayrollHistory = async (id) => {
    try {
        const response = await apiClient.get(`/payroll/history/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== AUDIT LOG SERVICES ====================

/**
 * Get system audit logs (immutable, read-only)
 * @param {Object} params - { adminName, category, actionType, severity, startDate, endDate, search, page, limit }
 */
export const getAuditLogs = async (params = {}) => {
    try {
        const response = await apiClient.get('/audit-logs', { params });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Get audit log statistics
 */
export const getAuditStats = async () => {
    try {
        const response = await apiClient.get('/audit-logs/stats');
        return response.data.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== INSTRUCTOR MANAGEMENT SERVICES ====================

/**
 * [Admin] Create a new instructor
 * @param {Object} data - { fullName, email, password, phone }
 */
export const createInstructor = async (data) => {
    try {
        const response = await apiClient.post('/instructor-mgmt/instructors', data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Get all instructors with student counts
 */
export const getAllInstructors = async () => {
    try {
        const response = await apiClient.get('/instructor-mgmt/instructors');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Get a single instructor with their students
 * @param {string} id
 */
export const getInstructorById = async (id) => {
    try {
        const response = await apiClient.get(`/instructor-mgmt/instructors/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Update instructor info
 * @param {string} id
 * @param {Object} data
 */
export const updateInstructor = async (id, data) => {
    try {
        const response = await apiClient.put(`/instructor-mgmt/instructors/${id}`, data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Delete an instructor
 * @param {string} id
 */
export const deleteInstructor = async (id) => {
    try {
        const response = await apiClient.delete(`/instructor-mgmt/instructors/${id}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Assign or unassign a student to an instructor
 * @param {string} studentId
 * @param {string|null} instructorId - null to unassign
 */
export const assignStudentToInstructor = async (studentId, instructorId) => {
    try {
        const response = await apiClient.patch('/instructor-mgmt/assign', { studentId, instructorId });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Bulk assign students to an instructor
 * @param {Array} studentIds
 * @param {string|null} instructorId
 */
export const bulkAssignStudents = async (studentIds, instructorId) => {
    try {
        const response = await apiClient.patch('/instructor-mgmt/assign-bulk', { studentIds, instructorId });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Get instructor statistics
 */
export const getInstructorStats = async () => {
    try {
        const response = await apiClient.get('/instructor-mgmt/stats');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Instructor] Get own dashboard data
 */
export const getInstructorDashboard = async () => {
    try {
        const response = await apiClient.get('/instructor-mgmt/dashboard');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Instructor] Get own assigned students list
 */
export const getMyStudents = async () => {
    try {
        const response = await apiClient.get('/instructor-mgmt/my-students');
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Instructor] Mark a student as present
 * @param {string} studentId
 * @param {number} durationInHours
 * @param {string} date - ISO date string (optional)
 * @param {string} notes - optional note
 */
export const markStudentPresent = async (studentId, durationInHours, date, notes) => {
    try {
        const response = await apiClient.post('/instructor-mgmt/mark-present', {
            studentId,
            durationInHours,
            date,
            notes
        });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Update any user account (except email/password/track)
 * @param {string} id
 * @param {Object} data 
 */
export const updateUser = async (id, data) => {
    try {
        const response = await apiClient.put(`/users/${id}`, data);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Recalculate student consumed and remaining hours
 * @param {string} studentId
 */
export const recalculateStudentHours = async (studentId) => {
    try {
        const response = await apiClient.patch(`/instructor-mgmt/recalculate/${studentId}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Instructor/Admin] Get session history for a student
 * @param {string} studentId
 */
export const getStudentSessions = async (studentId) => {
    try {
        const response = await apiClient.get(`/instructor-mgmt/sessions/${studentId}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * [Admin] Get aggregated session report
 * @param {Object} params - { startDate, endDate, instructorId }
 */
export const getAdminSessionReport = async (params = {}) => {
    try {
        const response = await apiClient.get('/instructor-mgmt/admin-report', { params });
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

/**
 * Delete user account permanently (Admin only)
 * @param {string} userId
 */
export const deleteUser = async (userId) => {
    try {
        const response = await apiClient.delete(`/users/${userId}`);
        return response.data;
    } catch (error) {
        throw handleError(error);
    }
};

// ==================== ACADEMIC SERVICES ====================
export const getAcademicDashboard = async () => { const res = await apiClient.get('/academic/dashboard'); return res.data?.data || res.data || {}; };

export const getAcademicCourses = async (params) => (await apiClient.get('/academic/courses', { params })).data;
export const getAcademicCourse = async (id) => (await apiClient.get(`/academic/courses/${id}`)).data.data;
export const createAcademicCourse = async (data) => (await apiClient.post('/academic/courses', data)).data.data;
export const updateAcademicCourse = async (id, data) => (await apiClient.put(`/academic/courses/${id}`, data)).data.data;
export const archiveAcademicCourse = async (id) => (await apiClient.delete(`/academic/courses/${id}`)).data;
export const createCourseLesson = async (courseId, data) => (await apiClient.post(`/academic/courses/${courseId}/lessons`, data)).data.data;
export const accessCourseLesson = async (id) => (await apiClient.post(`/academic/lessons/${id}/access`)).data.data;
export const updateCourseLesson = async (id, data) => (await apiClient.put(`/academic/lessons/${id}`, data)).data.data;
export const archiveCourseLesson = async (id) => (await apiClient.delete(`/academic/lessons/${id}`)).data;
export const getGroupSessions = async (groupId, params) => (await apiClient.get(`/academic/groups/${groupId}/sessions`, { params })).data.data;
export const getAttendanceSetup = async (groupId) => (await apiClient.get(`/academic/groups/${groupId}/attendance-setup`)).data.data;
export const createTeachingSession = async (data) => (await apiClient.post('/academic/sessions', data)).data.data;
export const updateTeachingSession = async (id, data) => (await apiClient.put(`/academic/sessions/${id}`, data)).data.data;
export const getSessionAttendance = async (id) => (await apiClient.get(`/academic/sessions/${id}/attendance`)).data.data;
export const saveSessionAttendance = async (id, records, options = {}) => (await apiClient.put(`/academic/sessions/${id}/attendance`, { records, ...options })).data;
export const getAttendanceSessions = async (params) => (await apiClient.get('/academic/attendance/sessions', { params })).data;
export const getAttendanceReport = async (params) => (await apiClient.get('/academic/attendance', { params })).data;
export const getCourseResults = async (courseId) => (await apiClient.get(`/academic/courses/${courseId}/results`)).data.data;
export const getExamResults = async (params = {}) => (await apiClient.get('/academic/results', { params })).data;
export const resetInstructorPassword = async (id, newPassword) => (await apiClient.patch(`/instructor-mgmt/instructors/${id}/reset-password`, { newPassword })).data;

// Export API client for advanced usage
export default apiClient;





